/**
 * Where every concept sits in the Knowledge Archive — a pure function of the
 * data, computed once, with no physics anywhere.
 *
 * ## Why the simulation had to go
 *
 * The force layout could never settle into anything readable, and the reason
 * is in the data rather than the solver: the backend writes a co-occurrence
 * edge between EVERY PAIR of concepts in a lesson
 * (`sync_task.py:302`), at a hardcoded `weight=1.0`, and
 * `MindspaceGraph` maps weight to link distance so that 1.0 is the SHORTEST
 * rest length. Each lesson is therefore a complete graph pulling itself into a
 * knot, and the knots shove each other around the canvas. No amount of charge
 * or collision tuning survives that.
 *
 * So position is assigned, not negotiated. Same data in, same arrangement out,
 * every reload — which also makes the view snapshot-testable, something the
 * force graph never was.
 *
 * ## Two stages
 *
 * 1. **Clusters** — one circle per subject, placed by `d3-hierarchy`'s
 *    `pack()`. Pack is given each cluster's radius explicitly (see
 *    `clusterRadius`) rather than a value to scale, so it only has to solve
 *    the packing, and wildly uneven subjects (400 concepts beside 3) nest
 *    without overlapping.
 * 2. **Concepts within a cluster** — a sunflower spiral (`spiralOffset`), a
 *    natural scatter. The lesson's key ideas are the stars of a REAL
 *    constellation (Orion, Cassiopeia, the Plough...; see `skyFigures`),
 *    placed in its shape and joined by its traditional lines, never the
 *    same figure as a lesson right beside it. `rank` puts the
 *    stars first, then importance; the level-of-detail gate thins by it (see
 *    `graphLod`), so zoomed out you see each lesson's figure.
 */

import { hierarchy, pack } from 'd3-hierarchy';
import { pickSkyFigure } from '@/lib/mindspace/skyFigures';

/**
 * Centre-to-centre spacing between adjacent nodes, in graph units. Has to
 * clear the widest node plus its label pill; 26 matches what `forceCollide`
 * was being asked for and leaves room for the label drop.
 */
export const NODE_SPACING = 26;

/**
 * Breathing room around a cluster, which is also where its label sits.
 *
 * Proportional to what the cluster holds rather than flat, and that is worth
 * more than it sounds. A flat 44 is most of a two-concept cluster's radius
 * (75 units, of which 26 is content), so with the hundreds of tiny clusters a
 * real archive produces the packed disc was mostly padding — it opened at 31%
 * and almost nothing was legible. Scaling it nearly doubles the opening zoom
 * to ~51% on the same data, with no change worth seeing on the big clusters
 * (77 concepts: 275 units before, 270 after).
 */
export const CLUSTER_PADDING_BASE = 8;
export const CLUSTER_PADDING_SCALE = 4;

/** Room around a cluster of `count` concepts, label included. */
export function clusterPadding(count: number): number {
  return CLUSTER_PADDING_BASE + CLUSTER_PADDING_SCALE * Math.sqrt(count);
}

/** The golden angle, 2π(2−φ) — what makes the spiral fill evenly. */
const GOLDEN_ANGLE = Math.PI * (3 - Math.sqrt(5));

/**
 * Deterministic scatter applied on top of the spiral.
 *
 * A pure phyllotaxis is *too* regular: past a few hundred nodes the eye picks
 * out the spiral arms and the cluster reads as a machined pattern. Nudging
 * each node by a hashed offset breaks the arms while staying a pure function
 * of the id, so the arrangement is still identical on reload.
 *
 * Bounded so it cannot undo the spacing guarantee. Adjacent nodes sit exactly
 * `NODE_SPACING` apart (26); two nodes of the largest radius need 12.8
 * between centres; 2 x JITTER must stay under the 13.2 of slack.
 */
const JITTER = 5;

/** A stable 0..1 from an id — same node, same nudge, every render. */
function hash01(id: string, salt: number): number {
  let h = salt;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return (Math.abs(h) % 1000) / 1000;
}

/**
 * The minimum a concept needs to be placed. Deliberately not
 * `KnowledgeGraphNode`: `subject` is resolved once by `decorateGraph` (via
 * `subjectOf`) so the layout and the labels can never disagree about which
 * cluster a concept belongs to, and the layout stays trivially testable.
 */
export interface ClusterInput {
  id: string;
  name: string;
  importance: number | null;
  subject: string;
}

export interface PlacedNode {
  id: string;
  subject: string;
  x: number;
  y: number;
  /**
   * Position within its own cluster, 0 = the centre and the subject's most
   * important concept. The level-of-detail gate thins a cluster by rank,
   * which works because offset radius grows as `sqrt(rank)` - hiding high
   * ranks hides the outside and keeps the core.
   *
   * It is also the field a future streaming version would get from the server
   * instead of computing here, which is what would let a node be placed
   * without loading the whole archive.
   */
  rank: number;
}

export interface ClusterCircle {
  subject: string;
  count: number;
  /** Centre, in the same space as the nodes. */
  x: number;
  y: number;
  r: number;
}

export interface LessonConstellation {
  /** The real constellation's name, e.g. "Orion". */
  name: string;
  lines: Array<[string, string]>;
}

export interface ClusterLayout {
  nodes: PlacedNode[];
  clusters: ClusterCircle[];
  /** Per subject, the real constellation it takes and its lines as
   *  concept-id pairs. */
  constellations: Record<string, LessonConstellation>;
}

/**
 * Offset of the i-th node from its cluster centre, with `id` deciding its
 * scatter: a sunflower (golden-angle) spiral, which spaces points evenly at
 * every radius and reads as a natural scatter of stars rather than a pattern.
 * Index 0 is the exact centre, unjittered.
 */
export function spiralOffset(index: number, id = ''): { x: number; y: number } {
  const radius = NODE_SPACING * Math.sqrt(index);
  const angle = index * GOLDEN_ANGLE;
  const base = { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
  if (index === 0) return base;
  return {
    x: base.x + (hash01(id, 7) * 2 - 1) * JITTER,
    y: base.y + (hash01(id, 13) * 2 - 1) * JITTER,
  };
}

/**
 * How many key ideas a lesson can put into its constellation: about the
 * square root of its size, between 3 and 9 (the largest figure, Scorpius).
 * The figure it takes is the biggest real constellation that fits
 * (`pickSkyFigure`). None below 3 concepts.
 */
export function constellationSize(count: number): number {
  if (count < 3) return 0;
  return Math.min(9, Math.max(3, Math.round(Math.sqrt(count)) + 1));
}

/** Lessons closer than this (gap between circles) count as neighbours, and
 *  never take the same constellation. */
const NEIGHBOUR_REACH = 200;

/** How far a scattered concept keeps from a constellation's star. */
const CLEAR = NODE_SPACING * 0.85;

/**
 * Radius a cluster needs to hold `count` nodes, including its padding. Room
 * for a few more than `count`: scatter spots too close to a constellation's
 * stars are skipped, which pushes the last concepts slightly outward.
 */
export function clusterRadius(count: number): number {
  const slots = count + constellationSize(count) * 2;
  const outermost = count <= 1 ? 0 : NODE_SPACING * Math.sqrt(slots - 1);
  // `+ JITTER` because a node on the rim may be nudged outward.
  return outermost + JITTER + clusterPadding(count);
}

/** Importance desc, name as a stable tiebreak so nothing ever reshuffles. */
function byImportance(a: ClusterInput, b: ClusterInput): number {
  return (
    (b.importance ?? -1) - (a.importance ?? -1) || a.name.localeCompare(b.name)
  );
}

/** A link between two concepts, by id, for pulling their lessons together. */
export interface ClusterLink {
  source: string;
  target: string;
}

/** Gap kept between two neighbouring clusters' circles, in graph units. */
export const CLUSTER_GAP = 16;
const RELAX_ITERATIONS = 240;

/**
 * Pull clusters that share links towards each other, without overlapping.
 *
 * `pack()` places lessons by size alone, so two lessons about the same thing
 * could sit on opposite sides of the map. This relaxes the packed positions:
 * each pair of clusters joined by links is drawn together in proportion to
 * how many links join them (relative to the strongest pair), every pair is
 * pushed apart until their circles clear `CLUSTER_GAP`, and a weak pull to
 * the centre keeps the map compact. Related lessons end up as neighbours, so
 * their neighbourhood reads as one region.
 *
 * Deterministic: it starts from pack's positions, visits pairs in a fixed
 * order and uses no randomness, so the same data gives the same map on every
 * visit. Mutates and returns `circles`.
 */
export function relaxClusters<
  T extends { subject: string; x: number; y: number; r: number },
>(circles: T[], affinity: ReadonlyMap<string, number>): T[] {
  const n = circles.length;
  if (n < 2) return circles;
  const index = new Map(circles.map((c, i) => [c.subject, i]));
  const pairs: Array<{ a: number; b: number; w: number }> = [];
  let maxW = 0;
  for (const [key, w] of affinity) {
    const [sa, sb] = key.split('\u0000');
    const a = index.get(sa);
    const b = index.get(sb);
    if (a == null || b == null || a === b) continue;
    pairs.push({ a, b, w });
    maxW = Math.max(maxW, w);
  }
  // No links still runs: the collision pass gives every map the same gap
  // between neighbouring lessons, whether or not anything pulls them.
  const iterations = pairs.length === 0 ? 40 : RELAX_ITERATIONS;
  for (let step = 0; step < iterations; step++) {
    const cool = 1 - step / iterations;
    // Attraction along shared links, towards touching distance.
    for (const { a, b, w } of pairs) {
      const A = circles[a];
      const B = circles[b];
      const dx = B.x - A.x;
      const dy = B.y - A.y;
      const d = Math.hypot(dx, dy) || 1;
      const rest = A.r + B.r + CLUSTER_GAP;
      if (d <= rest) continue;
      const pull = maxW ? ((d - rest) * 0.1 * cool * w) / maxW : 0;
      const share = B.r / (A.r + B.r);
      A.x += (dx / d) * pull * share;
      A.y += (dy / d) * pull * share;
      B.x -= (dx / d) * pull * (1 - share);
      B.y -= (dy / d) * pull * (1 - share);
    }
    // A weak pull to the centre, so unlinked lessons do not drift away.
    for (const c of circles) {
      c.x -= c.x * 0.004 * cool;
      c.y -= c.y * 0.004 * cool;
    }
    // Collision: no two circles closer than touching plus the gap.
    for (let i = 0; i < n; i++) {
      for (let j = i + 1; j < n; j++) {
        const A = circles[i];
        const B = circles[j];
        const dx = B.x - A.x;
        const dy = B.y - A.y;
        const min = A.r + B.r + CLUSTER_GAP;
        const d2 = dx * dx + dy * dy;
        if (d2 >= min * min) continue;
        const d = Math.sqrt(d2) || 1e-3;
        const push = (min - d) / 2;
        // Two exactly coincident centres separate along a fixed axis.
        const ux = d2 === 0 ? 1 : dx / d;
        const uy = d2 === 0 ? 0 : dy / d;
        A.x -= ux * push;
        A.y -= uy * push;
        B.x += ux * push;
        B.y += uy * push;
      }
    }
  }
  return circles;
}

/** Link counts between pairs of subjects, keyed `a\u0000b` with a < b. */
export function subjectAffinity(
  input: readonly ClusterInput[],
  links: readonly ClusterLink[]
): Map<string, number> {
  const subjectOf = new Map(input.map(n => [n.id, n.subject]));
  const out = new Map<string, number>();
  for (const link of links) {
    const a = subjectOf.get(link.source);
    const b = subjectOf.get(link.target);
    if (!a || !b || a === b) continue;
    const key = a < b ? `${a}\u0000${b}` : `${b}\u0000${a}`;
    out.set(key, (out.get(key) ?? 0) + 1);
  }
  return out;
}

export function layoutClusters(
  input: readonly ClusterInput[],
  links: readonly ClusterLink[] = []
): ClusterLayout {
  if (input.length === 0)
    return { nodes: [], clusters: [], constellations: {} };

  const bySubject = new Map<string, ClusterInput[]>();
  for (const node of input) {
    const subject = node.subject;
    const list = bySubject.get(subject);
    if (list) list.push(node);
    else bySubject.set(subject, [node]);
  }

  // Biggest first, then alphabetical: pack is sensitive to input order, so
  // this is what keeps the arrangement stable as concepts are added.
  const subjects = [...bySubject.entries()]
    .map(([subject, members]) => ({
      subject,
      members: [...members].sort(byImportance),
    }))
    .sort(
      (a, b) =>
        b.members.length - a.members.length ||
        a.subject.localeCompare(b.subject)
    );

  const root = hierarchy<{
    subject: string;
    r: number;
    children?: never[];
  }>({
    subject: '__root__',
    r: 0,
    children: subjects.map(s => ({
      subject: s.subject,
      r: clusterRadius(s.members.length),
    })) as never,
  });

  // `.radius()` means pack solves the arrangement only and leaves our radii
  // alone — without it pack derives radii from `value` and rescales to `size`,
  // which would untie node spacing from cluster size.
  const packed = pack<{ subject: string; r: number }>().radius(d => d.data.r)(
    root
  );

  const leaves = packed.leaves();
  // Pack works in a positive quadrant; recentre on the origin so the viewport
  // maths and `centerAt(0, 0)` keep working unchanged.
  const cx = packed.x ?? 0;
  const cy = packed.y ?? 0;

  const clusters: ClusterCircle[] = leaves.map(leaf => ({
    subject: leaf.data.subject,
    count: 0,
    x: leaf.x - cx,
    y: leaf.y - cy,
    r: leaf.r,
  }));
  // Then let lessons that share links move next to each other.
  relaxClusters(clusters, subjectAffinity(input, links));

  const nodes: PlacedNode[] = [];
  const constellations: Record<string, LessonConstellation> = {};
  for (const cluster of clusters) {
    const entry = subjects.find(s => s.subject === cluster.subject);
    if (!entry) continue;
    cluster.count = entry.members.length;
    const clusterX = cluster.x;
    const clusterY = cluster.y;

    // The lesson takes a real constellation that fits its key ideas, never
    // the same one as a lesson right beside it. Its most important ideas
    // sit on the figure's stars (the most important on the brightest), and
    // every other concept takes the next free spot of the scatter that is
    // not crowding a star. Stars rank first, so zoomed out you see the
    // figure.
    const n = entry.members.length;
    const neighbours = new Set<string>();
    for (const other of clusters) {
      const taken = constellations[other.subject];
      if (!taken || other === cluster) continue;
      const gap =
        Math.hypot(other.x - cluster.x, other.y - cluster.y) -
        other.r -
        cluster.r;
      if (gap < NEIGHBOUR_REACH) neighbours.add(taken.name);
    }
    const figure =
      constellationSize(n) > 0
        ? pickSkyFigure(constellationSize(n), entry.subject, neighbours)
        : null;
    const starCount = figure?.stars.length ?? 0;
    // The figure spans most of the lesson, but never less than enough room
    // for its stars to stay clear of each other.
    const span = Math.max(
      NODE_SPACING * 1.6,
      0.8 * NODE_SPACING * Math.sqrt(Math.max(1, n - 1))
    );
    const starAt = (figure?.stars ?? []).map(([x, y]) => ({
      x: x * span,
      y: y * span,
    }));

    let slot = 0;
    const nextFree = (id: string) => {
      for (;;) {
        const at = spiralOffset(slot++, id);
        if (starAt.every(st => Math.hypot(st.x - at.x, st.y - at.y) >= CLEAR))
          return at;
      }
    };
    const ids: string[] = [];
    entry.members.forEach((node, i) => {
      const offset = i < starCount ? starAt[i] : nextFree(node.id);
      nodes.push({
        id: node.id,
        subject: entry.subject,
        x: clusterX + offset.x,
        y: clusterY + offset.y,
        rank: i,
      });
      ids.push(node.id);
    });
    if (figure) {
      constellations[entry.subject] = {
        name: figure.name,
        lines: figure.lines.map(([a, b]) => [ids[a], ids[b]]),
      };
    }
  }

  return { nodes, clusters, constellations };
}
